<template>
  <div class="kpt-configurator-cart">
    <div class="configurator-cart m-4">
      <header class="p-4 is-flex is-justify-content-space-between is-align-items-center" :class="{ 'configurator-cart-header': !showPrices }">
        <h1 class="title mb-0">Shopping Cart</h1>
        <b-tooltip label="Empty the cart and return to the start screen." position="is-left">
          <b-button size="is-small" type="is-primary" :disabled="!cart.length" @click="startOver">Start Over</b-button>
        </b-tooltip>
      </header>
      <main v-if="cart.length">
        <!-- Header START -->
        <template v-if="showPrices">
          <div class="kpt-configurator-item pb-2" v-if="wholesale">
            <div></div>
            <div></div>
            <h6 class="mr-2 has-text-right"><strong>Estimated Wholesale Price</strong></h6>
            <h6 class="mr-2 has-text-right"><strong>Estimated <br>MSRP Price</strong></h6>
          </div>
          <div class="kpt-configurator-divider has-text-right mx-2 pb-2" v-else>
            <h6 class="mr-2"><strong>Estimated <br>Retail Price</strong></h6>
          </div>
        </template>
        <!-- Header END -->
        <!-- Cart Item START -->
        <div v-for="(cartItem, idx) in cart" :key="idx" class="mx-2 mt-2 kpt-configurator-item-wrapper">
          <div class="kpt-configurator-item">
            <!-- Image -->
            <figure class="p-4" v-if="getItemAsset(cartItem) == null">
              <b-image :src="require(`@/assets/configurator/placeholder.png`)"></b-image>
            </figure>
            <figure class="p-4" v-else>
              <b-image :src="`${assetCdnUrl}/${getItemAsset(cartItem)}`"></b-image>
            </figure>
            <!-- Description -->
            <div>
              <h4 class="mb-1"><strong>{{cartItem.item?.name}}</strong></h4>
              <!-- If the item is an accessory, we just say so... -->
              <span v-if="isAccessory(cartItem.kpc)">Accessory</span>
              <!-- ...otherwise we have a full on description, starting with hardware and connectivity -->
              <div class="is-flex mt-2" v-else>
                <!-- List the hardware first -->
                <div class="pr-6" style="min-width: 250px">
                  <h6 class="is-size-7 is-uppercase has-text-weight-bold">Hardware</h6>
                  <ul>
                    <li v-if="cartItem.item.modem !== 'none'">{{getOption(cartItem.item.modem)?.name}}</li>
                    <li>{{getOption(cartItem.item.buc)?.name}}</li>
                    <li v-if="cartItem.item.sdwan">{{getOption(cartItem.item.sdwan)?.name}}</li>
                    <li v-if="cartItem.item.case">{{getOption(cartItem.item.case)?.name}}</li>
                  </ul>
                </div>
                <!-- To the right, show the connectivity text -->
                <div v-if="cartItem.connectivity">
                  <h6 class="is-size-7 is-uppercase has-text-weight-bold">Connectivity</h6>
                  <div v-if="cartItem.connectivity.custom">
                    <span>{{cartItem.connectivity.custom}}</span><br><br>
                    <p class="is-size-7"><span v-if="isFinanced(cartItem)">The price displayed does not include the cost of service. </span>A Kymeta sales associate will contact you shortly regarding your custom plan needs.</p>
                  </div>
                  <p v-else-if="!cartItem.connectivity.custom && cartItem.connectivity.kpc">{{getProduct(cartItem.connectivity.kpc)?.name}}</p>
                  <p v-else>None selected.</p>
                </div>
              </div>
              <!-- Next, show the warranty under the hardware and connectivity -->
              <div class="mt-2" v-if="cartItem.warranty">
                <h6 class="is-size-7 is-uppercase has-text-weight-bold">Warranty</h6>
                <p>{{cartItem.warranty.count > 0 ? `${cartItem.warranty.count} extra year${cartItem.warranty.count > 1 ? 's' : ''} of ` : ''}}{{getProduct(cartItem.warranty.kpc)?.name}}</p>
              </div>
              <!-- Then, show the quantity selector -->
              <br>
              <section class="kpt-configurator-cart-qty-mdfr">
                <label class="label">Quantity</label>
                <b-field style="width: 125px">
                  <b-numberinput 
                    size="is-small" 
                    min="1"
                    max="999"
                    v-model="cartItem.quantity"></b-numberinput>
                </b-field>
                <span>
                  <b-button rounded type="is-light" size="is-small" @click="deleteFromCart(cartItem)">DELETE</b-button>
                </span>
                <span v-if="isTerminal(cartItem.kpc)">
                  <b-button rounded size="is-small" type="is-primary is-outlined" @click="editCartItem(cartItem)">EDIT</b-button>
                </span>
              </section>
              <!-- Lastly, show financing option if customer picked connectivity during terminal creation -->
              <!-- <section class="kpt-configurator-cart-financing" v-if="isTerminal(cartItem.kpc) && hasConnectivity(cartItem) && isNotMilitarySpec(cartItem.kpc)">
                <label class="label">Financing</label>
                <div>
                  <div class="is-flex">
                    <template v-for="fo in availableFinancingOptions">
                      <b-radio-button
                        @input="verifyWarranty(cartItem)"
                        :key="fo.key"
                        v-model="cartItem.financing"
                        :native-value="fo.key"
                        type="is-primary is-light is-outlined"
                        class="mb-1 has-text-black-bis has-text-weight-bold">
                        <span style="width: 100%" class="has-text-left">{{fo.value}}</span>
                      </b-radio-button>
                    </template>
                  </div>
                  <p class="is-size-7">Note: When financing, terminal warranty plans must match or exceed the length of payment terms. Only Hardware can be financed (connectivity and warranty cannot be financed).</p>
                </div>
              </section> -->
              <template v-if="isTerminal(cartItem.kpc) && cartItem.warranty.originalCount != cartItem.warranty.count">
                <b-message type="is-warning" class="mb-4">
                  Bundle warranty was updated automatically to match financing term.
                </b-message>
              </template>
            </div>
            <!-- End Description -->
            <!-- Price (first div = wholesale, second div = msrp) -->
            <div class="mr-2">
              <!-- We hide this column but keep the parent div to maintain grid -->
              <div v-if="wholesale && showPrices">
                <!-- If the item is an accessory, or is a terminal without connectivity or financing -->
                <template v-if="isAccessory(cartItem.kpc) || (isTerminal(cartItem.kpc) && !isFinanced(cartItem) && !hasConnectivity(cartItem))">
                  <div v-if="getCartItemTotals(cartItem)[0] == 0">
                    <h4 class="has-text-right has-text-weight-semibold">N/A</h4>
                  </div>
                  <div v-else>
                    <h4 class="has-text-right has-text-weight-semibold">{{$numeral(getCartItemTotals(cartItem)[0]).format('$0,0')}}</h4>
                    <h6 class="has-text-right">Unit Price: {{$numeral(getCartItemUnitPrice(cartItem)[0]).format('$0,0')}}</h6>
                  </div>
                </template>
                <!-- If the item is a terminal with connectivity but no financing -->
                <template v-if="isTerminal(cartItem.kpc) && hasConnectivity(cartItem) && !isFinanced(cartItem)">
                  <div v-if="getCartItemTotals(cartItem)[0] == 0">
                    <h4 class="has-text-right has-text-weight-semibold">N/A</h4>
                  </div>
                  <div v-else>
                    <h4 class="has-text-right has-text-weight-semibold">{{$numeral(getCartItemTotals(cartItem)[0]).format('$0,0')}}</h4>
                    <h6 class="has-text-right">Unit Price: {{$numeral(getCartItemUnitPrice(cartItem)[0]).format('$0,0')}}</h6>
                    <h6 class="has-text-right">Monthly Connectivity Price: {{!cartItem.connectivity.custom ? $numeral(getCartItemConnectivityPrice(cartItem)[0]).format('$0,0') : 'TBD'}}</h6>
                  </div>
                </template>
                <!-- If the item is a terminal with connectivity and financing -->
                <template v-if="isTerminal(cartItem.kpc) && hasConnectivity(cartItem) && isFinanced(cartItem)">
                  <div v-if="getCartItemTotals(cartItem)[0] == 0">
                    <h4 class="has-text-right has-text-weight-semibold">N/A</h4>
                  </div>
                  <div v-else>
                    <!-- Show the monthly payments first -->
                    <h4 class="has-text-right has-text-weight-semibold">{{$numeral(getCartItemMonthly(cartItem)[0]).format('$0,0')}}</h4>
                    <h6 class="has-text-right">/ month</h6>
                    <!-- Show the upfront cost second -->
                    <h4 class="has-text-right has-text-weight-semibold mt-3">{{$numeral(getCartItemUpfrontCost(cartItem)[0]).format('$0,0')}}</h4>
                    <h6 class="has-text-right">upfront cost</h6>
                  </div>
                </template>
              </div>
            </div>
            <!-- MSRP -->
            <div class="mr-2">
              <div v-if="showPrices">
                <!-- If the item is an accessory, or is a terminal without connectivity or financing -->
                <template v-if="isAccessory(cartItem.kpc) || (isTerminal(cartItem.kpc) && !isFinanced(cartItem) && !hasConnectivity(cartItem))">
                  <div v-if="getCartItemTotals(cartItem)[1] == 0">
                    <h4 class="has-text-right has-text-weight-semibold">N/A</h4>
                  </div>
                  <div v-else>
                    <h4 class="has-text-right has-text-weight-semibold">{{$numeral(getCartItemTotals(cartItem)[1]).format('$0,0')}}</h4>
                    <h6 class="has-text-right">Unit Price: {{$numeral(getCartItemUnitPrice(cartItem)[1]).format('$0,0')}}</h6>
                  </div>
                </template>
                <!-- If the item is a terminal with connectivity but no financing -->
                <template v-if="isTerminal(cartItem.kpc) && hasConnectivity(cartItem) && !isFinanced(cartItem)">
                  <div v-if="getCartItemTotals(cartItem)[1] == 0">
                    <h4 class="has-text-right has-text-weight-semibold">N/A</h4>
                  </div>
                  <div v-else>
                    <h4 class="has-text-right has-text-weight-semibold">{{$numeral(getCartItemTotals(cartItem)[1]).format('$0,0')}}</h4>
                    <h6 class="has-text-right">Unit Price: {{$numeral(getCartItemUnitPrice(cartItem)[1]).format('$0,0')}}</h6>
                    <h6 class="has-text-right">Monthly Connectivity Price: {{!cartItem.connectivity.custom ? $numeral(getCartItemConnectivityPrice(cartItem)[1]).format('$0,0') : 'TBD'}}</h6>
                  </div>
                </template>
                <!-- If the item is a terminal with connectivity and financing -->
                <template v-if="isTerminal(cartItem.kpc) && hasConnectivity(cartItem) && isFinanced(cartItem)">
                  <div v-if="getCartItemTotals(cartItem)[1] == 0">
                    <h4 class="has-text-right has-text-weight-semibold">N/A</h4>
                  </div>
                  <div v-else>
                    <!-- Show the monthly payments first -->
                    <h4 class="has-text-right has-text-weight-semibold">{{$numeral(getCartItemMonthly(cartItem)[1]).format('$0,0')}}</h4>
                    <h6 class="has-text-right">/ month</h6>
                    <!-- Show the upfront cost second -->
                    <h4 class="has-text-right has-text-weight-semibold mt-3">{{$numeral(getCartItemUpfrontCost(cartItem)[1]).format('$0,0')}}</h4>
                    <h6 class="has-text-right">upfront cost</h6>
                  </div>
                </template>
              </div>
            </div>
          </div>
          <div v-if="productIsUnavailable(cartItem)">
            <p class="has-text-danger has-text-right mb-2">
              <span class="has-text-danger has-text-weight-bold mr-1">*</span>
              This product may not be available at this time. Please contact your sales person.
            </p>
          </div>
        </div>
        
        <!-- Cart Item END -->
        <!-- End Price -->
        <!-- If financing is selected at all for any terminal, or if connectivity is selected -->
        <template v-if="showPrices">
          <template v-if="showFinancingTotals">
            <div class="kpt-configurator-item p-4 pb-0" style="border-bottom: 0;">
              <div></div>
              <h4 class="is-flex is-justify-content-flex-end is-align-items-flex-end">Estimated Monthly Total:</h4>
              <div class="has-text-right">
                <template v-if="wholesale">
                  <span class="is-uppercase is-size-7">Wholesale</span>
                  <h5 class="has-text-weight-semibold">{{cartMonthlyTotal.wholesale ? $numeral(cartMonthlyTotal.wholesale).format('$0,0') : 'N/A'}}</h5>
                </template>
              </div>
              <div class="has-text-right">
                <span class="is-uppercase is-size-7">{{wholesale ? 'MSRP' : 'Retail'}}</span>
                <h5 class="has-text-weight-semibold">{{cartMonthlyTotal.msrp ? $numeral(cartMonthlyTotal.msrp).format('$0,0') : 'N/A'}}</h5>
              </div>
            </div>
            <div class="kpt-configurator-item pt-0 px-4 pb-4">
              <div></div>
              <h4 class="is-flex is-justify-content-flex-end is-align-items-flex-end">Estimated Upfront Total:</h4>
              <div class="has-text-right">
                <template v-if="wholesale">
                  <h5 class="has-text-weight-semibold">{{cartUpfrontTotal.wholesale ? $numeral(cartUpfrontTotal.wholesale).format('$0,0') : 'N/A'}}</h5>
                </template>
              </div>
              <div class="has-text-right">
                <h5 class="has-text-weight-semibold">{{cartUpfrontTotal.msrp ? $numeral(cartUpfrontTotal.msrp).format('$0,0') : 'N/A'}}</h5>
              </div>
            </div>
          </template>
          <!-- No financing selected -->
          <template v-else>
            <div class="kpt-configurator-item p-4" style="border-bottom: 0">
              <div></div>
              <h4 class="is-flex is-justify-content-flex-end is-align-items-flex-end">Estimated Total:</h4>
              <div class="has-text-right">
                <template v-if="wholesale">
                  <span class="is-uppercase is-size-7">Wholesale</span>
                  <h5 class="has-text-weight-semibold">{{cartTotal.wholesale ? $numeral(cartTotal.wholesale).format('$0,0') : 'N/A'}}</h5>
                </template>
              </div>
              <div class="has-text-right">
                <span class="is-uppercase is-size-7">{{wholesale ? 'MSRP' : 'Retail'}}</span>
                <h5 class="has-text-weight-semibold">{{cartTotal.msrp ? $numeral(cartTotal.msrp).format('$0,0') : 'N/A'}}</h5>
              </div>
            </div>
          </template>
          <div class="p-4">
            <p><strong>NOTICE:</strong> Quotes are subject to change based on additional requested products and services. Terms and conditions apply.</p>
          </div>
        </template>
      </main>
      <div class="py-6" v-else>
        <p class="has-text-centered">Your shopping cart is empty.</p>
      </div>
    </div>
    <div class="configurator-upsell m-4 p-4">
      <h4><strong>Looking for more?</strong></h4>
      <div class="configurator-upsell-grid">
        <div class="configurator-upsell-terminal">
          <figure class="image">
            <img :src="require(`@/assets/configurator/u8-antenna.png`)" />
          </figure>
          <p class="mb-2">Configure another terminal?</p>
          <b-button rounded size="is-small" type="is-dark" @click="() => { $store.commit('configurator/setActivePage', 1); $kya.event('configurator', 'openConfigureTerminalFromCart', 'Started terminal configuration from the cart.', am); }">CONFIGURE TERMINAL</b-button>
        </div>
        <div class="configurator-upsell-divider"></div>
        <div class="configurator-upsell-accessory">
          <b-image :src="require(`@/assets/configurator/accessories/u8acc-00044.png`)"></b-image>
          <p class="mb-2">Additional Accessories Coming Soon!</p>
          <!-- <b-button rounded size="is-small" type="is-dark" @click="() => { $store.commit('configurator/setActivePage', 3); $kya.event('configurator', 'openAddAccessoriesFromCart', 'Opened Accessories page from the cart.', am); }">EXPLORE ACCESSORIES</b-button> -->
        </div>
      </div>
    </div>
    <div class="configurator-contact m-4 p-4">
      <div class="is-flex mb-4">
        <b-field label="Ship To Country for this Order" required>
          <b-select v-model="shippingCountry" expanded>
            <option :value="country" v-for="country in ConfiguratorCountries" :key="country">{{country}}</option>
          </b-select>
        </b-field>
        <b-field v-if="shippingCountryIsOther" :required="shippingCountryIsOther" label="Please enter your country" class="ml-4">
          <b-input v-model="otherShippingCountry" type="text" maxlength="255" :has-counter="false" />
        </b-field>
      </div>
      
      <b-field label="Additional Notes">
        <b-input type="textarea" v-model="additionalNotes"></b-input>
      </b-field>
      <div class="configurator-contact-information">
        <b-field label="Contact Name" required>
          <b-input expanded type="text" v-model="contactName"></b-input>
        </b-field>
        <b-field label="Contact Email">
          <b-input type="email" v-model="contactEmail"></b-input>
        </b-field>
        <b-field label="Company Name" required>
          <b-input type="text" v-model="contactCompany"></b-input>
        </b-field>
      </div>
      <div class="has-text-right py-2">
        <b-button rounded class="is-uppercase" :loading="submittingOrder" type="is-primary" @click="submitOrder">SUBMIT QUOTE REQUEST</b-button>
      </div>
    </div>
  </div>
</template>
<script>
import { uniqBy } from 'lodash';
import { ref, computed, getCurrentInstance } from 'vue';
import ConfiguratorCountries from '../../assets/configurator/configurator-countries.json';

export default {
  setup() {
    const app = getCurrentInstance();
    const root = app.proxy.$root;
    const am = computed(() => root.$store.getters['configurator/analyticsMetadata']);
    const cart = computed(() => root.$store.state.configurator.cart);
    const wholesale = computed(() => root.$store.state.configurator.wholesale);
    const allProductsIndex = computed(() => root.$store.getters['configurator/allProductsIndex']);
    const allProducts = computed(() => root.$store.getters['configurator/allProducts']);
    const availableAccessories = computed(() => root.$store.getters['configurator/accessories']);
    const discountTier = computed(() => root.$store.getters['configurator/discountTier']);
    const showFinancingTotals = computed(() => cart.value.some(c => c.connectivity && c.connectivity.kpc));
    const showPrices = computed(() => root.$store.state.configurator.showPrices);
    const configuratorRules = computed(() => root.$store.getters['configurator/rules']);

    const shippingCountry = ref('United States');
    const otherShippingCountry = ref('');
    const additionalNotes = ref(null);
    const contactName = ref(null);
    const contactEmail = ref(null);
    const contactCompany = ref(null);
    const submittingOrder = ref(false);

    const getProduct = (kpc) => allProductsIndex.value[kpc];
    // This computed property goes up to 3 levels down the rules file tree,
    // grabs all arrays, then flattens them (3 times, 1 for each level) to return
    // a list of { id, name } objects, then used below in getOption function.
    const productOptions = computed(() => {
      const rules = configuratorRules.value;
      const applications = Object.values(rules).map(app => {
        const justApplicationKeys = Object.keys(app).filter(k => k != 'name');
        return justApplicationKeys.map(key => app[key]);
      }).flat();

      const availableHardware = applications.map(hw => {
        const justHardwareKeys = Object.keys(hw).filter(k => k != 'name');
        return justHardwareKeys.map(key => {
          // if it's an array, return it immediately
          if (hw[key].length) {
            return hw[key];
          }
          // otherwise go down one more level
          return Object.values(hw[key]);
        })
      });

      const flattened = availableHardware.flat().flat().flat();
      const deduplicated = uniqBy(flattened, 'id');
      return deduplicated;
    });
    const getOption = (optionId) => productOptions.value.find(o => o.id === optionId) || {};
    const isTerminal = (kpc) => getProduct(kpc).productFamily === 'terminal' || getProduct(kpc).productFamily === 'odu'; // ODUs are considered terminals even if they don't have a modem
    const accessoryFamilies = ['accessories', 'cables', 'components'];
    const isAccessory = (kpc) => accessoryFamilies.includes(getProduct(kpc).productFamily);
    const isFinanced = (cartItem) => cartItem.financing > 1;
    const isNotMilitarySpec = (kpc) => kpc !== 'U8711-10000-0';
    const hasConnectivity = (cartItem) => cartItem.connectivity && (cartItem.connectivity.kpc || !!(cartItem.connectivity.custom));

    // #region These totals are for unfinanced and no-connectivity items
    const getCartItemTotals = (cartItem) => {
      // get the base product first
      const baseProduct = getProduct(cartItem.kpc);
      if (!baseProduct) return [0, 0];
      // if it's not a terminal, it's an accessory, so return the prices
      if (isAccessory(cartItem.kpc)) return [baseProduct.wholesalePrice * cartItem.quantity, baseProduct.msrpPrice * cartItem.quantity];
      // otherwise get connectivity cost
      const connectivityCost = getProduct(cartItem.connectivity?.kpc);
      const warrantyCost = getProduct(cartItem.warranty?.kpc);
      // (base price + connectivity price if any + (warranty price * number of extra years)) * quantity of terminals
      return [
        (baseProduct.wholesalePrice + (connectivityCost ? connectivityCost?.wholesalePrice : 0) + (warrantyCost?.wholesalePrice * cartItem.warranty?.count)) * cartItem.quantity,
        (baseProduct.msrpPrice + (connectivityCost ? connectivityCost?.msrpPrice : 0) + (warrantyCost?.msrpPrice * cartItem.warranty?.count)) * cartItem.quantity
      ];
    }
    const getCartItemUnitPrice = (cartItem) => {
      const baseProduct = getProduct(cartItem.kpc);
      if (!baseProduct) return [0, 0];
      if (isAccessory(cartItem.kpc)) return [baseProduct.wholesalePrice, baseProduct.msrpPrice];
      const warrantyCost = getProduct(cartItem.warranty?.kpc);
      return [
        // CLDSRV-10609 - CR - remove connectivity price from unit price
        (baseProduct.wholesalePrice + (warrantyCost?.wholesalePrice * cartItem.warranty?.count)),
        (baseProduct.msrpPrice + (warrantyCost?.msrpPrice * cartItem.warranty?.count))
      ];
    }
    // #endregion

    // #region This total is for a terminal with connectivity
    const getCartItemConnectivityPrice = (cartItem) => {
      if (!isTerminal(cartItem.kpc)) return [];
      if (cartItem.connectivity?.custom) return [0, 0];
      const connectivityCost = getProduct(cartItem.connectivity.kpc);
      return [
        connectivityCost?.wholesalePrice * cartItem.quantity,
        connectivityCost?.msrpPrice * cartItem.quantity
      ]
    }
    // #endregion

    const getCartItemMonthly = (cartItem) => {
      // grab the prices first
      const connectivityItem = getProduct(cartItem.connectivity?.kpc);
      // monthly price = (financing monthly cost + connectivity cost) * quantity of terminals
      const monthlyWholesalePrice = (connectivityItem?.wholesalePrice || 0) * cartItem.quantity;
      const monthlyMsrpPrice = (connectivityItem?.msrpPrice || 0) * cartItem.quantity;
      return [monthlyWholesalePrice, monthlyMsrpPrice];
    }
    const getCartItemUpfrontCost = (cartItem) => {
      // grab the prices first
      const connectivityItem = getProduct(cartItem.connectivity?.kpc);
      const warrantyItem = getProduct(cartItem.warranty?.kpc);
      // (monthly financing price + connectivity price + full warranty cost) * quantity of terminals
      const upfrontWholesalePrice = ((connectivityItem?.wholesalePrice || 0) + (warrantyItem?.wholesalePrice * cartItem.warranty?.count)) * cartItem.quantity;
      const upfrontMsrpPrice = ((connectivityItem?.msrpPrice || 0) + (warrantyItem?.msrpPrice * cartItem.warranty?.count)) * cartItem.quantity;
      return [upfrontWholesalePrice, upfrontMsrpPrice]
    }

    // total without financing
    const cartTotal = computed(() => ({
      msrp: cart.value.map(cartItem => getCartItemTotals(cartItem)[1]).reduce((a, b) => a + b),
      wholesale: cart.value.map(cartItem => getCartItemTotals(cartItem)[0]).reduce((a, b) => a + b)
    }));
    
    // monthly totals should include
    // - anything that has connectivity or
    // - anything that is financed
    const cartMonthlyTotal = computed(() => ({
      msrp: cart.value
        .filter(cartItem => isTerminal(cartItem.kpc) && (hasConnectivity(cartItem) || isFinanced(cartItem)))
        .map(cartItem => getCartItemMonthly(cartItem)[1]).reduce((a, b) => a + b),
      wholesale: cart.value
        .filter(cartItem => isTerminal(cartItem.kpc) && (hasConnectivity(cartItem) || isFinanced(cartItem)))
        .map(cartItem => getCartItemMonthly(cartItem)[0]).reduce((a, b) => a + b)
    }));
    // upfront total should include
    // - any accessories
    // - any terminals that aren't financed and have no connectivity
    // - any terminals that aren't financed with connectivity
    // - upfront total of anything that is financed
    const cartUpfrontTotal = computed(() => {
      // #region MSRP
      const nonFinancedMsrpTotals = cart.value
        .filter(cartItem => isAccessory(cartItem.kpc) 
          || (isTerminal(cartItem.kpc) && !hasConnectivity(cartItem) && !isFinanced(cartItem)) 
          || (isTerminal(cartItem.kpc) && hasConnectivity(cartItem) && !isFinanced(cartItem)))
        .map(cartItem => getCartItemTotals(cartItem)[1])
        .reduce((a, b) => a + b, 0);
      const financedMsrpTotals = cart.value
        .filter(cartItem => isTerminal(cartItem.kpc) && hasConnectivity(cartItem) && isFinanced(cartItem))
        .map(cartItem => getCartItemUpfrontCost(cartItem)[1])
        .reduce((a, b) => a + b, 0);
      // #endregion

      // #region Wholesale
      const nonFinancedWholesaleTotals = cart.value
        .filter(cartItem => isAccessory(cartItem.kpc) 
          || (isTerminal(cartItem.kpc) && !hasConnectivity(cartItem) && !isFinanced(cartItem)) 
          || (isTerminal(cartItem.kpc) && hasConnectivity(cartItem) && !isFinanced(cartItem)))
        .map(cartItem => getCartItemTotals(cartItem)[0])
        .reduce((a, b) => a + b, 0);
      const financedWholesaleTotals = cart.value
        .filter(cartItem => isTerminal(cartItem.kpc) && hasConnectivity(cartItem) && isFinanced(cartItem))
        .map(cartItem => getCartItemUpfrontCost(cartItem)[0])
        .reduce((a, b) => a + b, 0);
      // #endregion

      return {
        msrp: nonFinancedMsrpTotals + financedMsrpTotals,
        wholesale: nonFinancedWholesaleTotals + financedWholesaleTotals
      }
    });

    const shippingCountryIsOther = computed(() => shippingCountry.value === 'Other');
    const deleteFromCart = (item) => {
      root.$kya.event('configurator', 'deleteFromCart', 'Deleted an item from the shopping cart.', { ...am.value, item });
      root.$store.commit('configurator/removeFromCart', { id: item.id });
    }
    const availableFinancingOptions = [{ key: 1, value: 'None' }, { key: 12, value: '12-month' }, { key: 24, value: '24-month' }, { key: 36, value: '36-month' }];

    const emptyCart = () => {
      root.$store.commit('configurator/emptyCart');
    }

    const startOver = () => {
      root.$buefy.dialog.confirm({
        type: 'is-warning',
        hasIcon: true,
        cancelText: 'No',
        confirmText: 'Yes',
        message: 'Starting over will clear your cart and reset your session. Are you sure you want to start over?',
        onConfirm: () => {
          root.$kya.event('configurator', 'startOverFromCart', 'Clicked on Start Over in the shopping cart.', { ...am.value, cart: cart.value });
          root.$store.commit('configurator/setCartItemUnderEdit', null);
          root.$store.commit('configurator/emptyCart');
          root.$store.commit('configurator/setActivePage', 0);
        }
      });
    }

    const editCartItem = (cartItem) => {
      root.$kya.event('configurator', 'editCartItem', 'Opened the Edit page for a cart item', { ...am.value, item: cartItem });
      root.$store.commit('configurator/setCartItemUnderEdit', cartItem);
      root.$store.commit('configurator/setActivePage', 1);
    }

    const verifyWarranty = (cartItem) => {
      // if financing is greater than warranty, show a popup
      const financingMonths = cartItem.financing;
      const warrantyMonths = (cartItem.warranty.count + 1) * 12;
      if (financingMonths > warrantyMonths) {
        // show a pop up
        root.$buefy.dialog.confirm({
          type: 'is-warning',
          hasIcon: true,
          cancelText: 'No',
          confirmText: 'Yes',
          message: 'Requires an update to the warranty term to match the financing term. Do you accept the change?',
          onConfirm: () => { // if user confirms, we change the warranty.count
            // eslint-disable-next-line no-param-reassign
            cartItem.warranty.count = (cartItem.financing / 12) - 1;
            // eslint-disable-next-line no-param-reassign
            cartItem.originalBuiltTerminal.warranty = `${cartItem.financing}`;
          },
          onCancel: () => { // if user rejects, we set the financing back to the original form
            // eslint-disable-next-line no-param-reassign
            cartItem.financing = cartItem.originalFinancing;
          }
        });
      }
    }

    const productIsUnavailable = (cartItem) => {
      const unavailableKPCs = allProducts.value.filter(p => p.unavailable).map(p => p.kpc);
      return unavailableKPCs.includes(cartItem.kpc);
    }

    const submitOrder = async () => {
      // prevent click spamming
      if (submittingOrder.value) return;

      if (!contactName.value || !contactCompany.value || !shippingCountry.value || (shippingCountry.value == 'Other' && !otherShippingCountry.value)) {
        root.$buefy.dialog.alert({
          title: 'Incomplete Form',
          message: 'Please fill out the required contact information prior to submitting your order.',
          type: 'is-danger',
          hasIcon: true,
          icon: 'exclamation-circle',
          ariaRole: 'alertdialog',
          ariaModal: true
        });
        return;
      }

      // if (!refs.contactEmail.checkHtml5Validity()) {
      //   console.log('invalid');
      //   return;
      // }

      if (!!(contactEmail.value) && !contactEmail.value.split('@')[1].includes('.')) {
        root.$buefy.dialog.alert({
          title: 'Invalid Email',
          message: 'Submitted e-mail address is invalid.',
          type: 'is-danger',
          hasIcon: true,
          icon: 'exclamation-circle',
          ariaRole: 'alertdialog',
          ariaModal: true
        });
        return;
      }

      // In the cart, for each item, expand the accessories and include the group Id
      // The items should look like:
      // KPN - Description - GroupID - Quantity
      // Example
      // U8911-11114-0 - KYMETA U8 GEO GO, 20 W BUC, IQ 200*, LTE ADVANCED PRO (GLOBAL) - 1 - 1
      // U8ACC-00002-0 - AC-to-DC Power, 25' (7.5M) Cables - 1 - 3
      // and so on..

      const purchaseOrder = {
        name: contactName.value,
        company: contactCompany.value,
        email: contactEmail.value,
        notes: additionalNotes.value,
        shippingCountry: shippingCountry.value == 'Other' ? otherShippingCountry.value : shippingCountry.value,
        order: [],
        connectivity: [],
        financing: [],
        discountTier: discountTier.value
      }

      purchaseOrder.msrpTotal = cartTotal.value.msrp;
      if (wholesale) purchaseOrder.wholesaleTotal = cartTotal.value.wholesale;

      // items in the cart request financing
      if (showFinancingTotals.value) {
        purchaseOrder.msrpMonthlyTotal = cartMonthlyTotal.value.msrp;
        if (wholesale) purchaseOrder.wholesaleMonthlyTotal = cartMonthlyTotal.value.wholesale;
      }

      // set the upfront totals so they are always displayed in the quote email notification (Summary)
      purchaseOrder.msrpUpfrontTotal = cartUpfrontTotal.value.msrp;
      if (wholesale) purchaseOrder.wholesaleUpfrontTotal = cartUpfrontTotal.value.wholesale;

      for (let i = 0; i < cart.value.length; i++) {
        const cartItem = cart.value[i];
        const product = getProduct(cartItem.kpc);

        const orderItem = {
          kpc: cartItem.kpc,
          type: product.productFamily,
          description: product.name,
          price: getCartItemUnitPrice(cartItem),
          quantity: cartItem.quantity,
          bundleId: i + 1
        }
        purchaseOrder.order.push(orderItem);
        // eslint-disable-next-line no-continue
        if (isAccessory(cartItem.kpc)) continue;
        
        // get all the accessories
        if (cartItem.item.accessories?.length) {
          cartItem.item.accessories.forEach(accessory => {
            const relevantAccessory = availableAccessories.value.find(a => a.id == accessory);
            if (!relevantAccessory) return;
            purchaseOrder.order.push({
              kpc: relevantAccessory.kpc,
              price: getCartItemUnitPrice(relevantAccessory),
              description: relevantAccessory.name,
              quantity: cartItem.quantity,
              bundleId: i + 1
            });
          });
        }

        // get the warranty
        if (cartItem.warranty && cartItem.warranty.count > 0) {
          const warrantyObject = getProduct(cartItem.warranty.kpc);
          purchaseOrder.order.push({
            kpc: cartItem.warranty.kpc,
            description: warrantyObject.name,
            quantity: cartItem.quantity * cartItem.warranty.count, // terminal count * extra years of warranty
            bundleId: i + 1
          });
        }

        // get the connectivity
        if (hasConnectivity(cartItem)) {
          const connectivityItem = getProduct(cartItem.connectivity.kpc);
          purchaseOrder.connectivity.push({
            kpc: cartItem.connectivity.kpc,
            // price: getCartItemUnitPrice(cartItem),
            description: connectivityItem?.name,
            bundleId: i + 1
          });
        }

        // get the financing
        if (isFinanced(cartItem)) {
          purchaseOrder.financing.push({
            duration: `${cartItem.financing} months`,
            bundleId: i + 1
          })
        }
      }

      try {
        // Trigger submission
        submittingOrder.value = true;
        const submitResult = await root.$store.dispatch('configurator/submitPurchaseOrder', purchaseOrder);
        submittingOrder.value = false;

        if (!submitResult) {
          root.$buefy.snackbar.open({
            message: 'There was an error submitting your request.',
            type: 'is-danger',
            position: 'is-bottom',
            actionText: 'OK',
            indefinite: false
          });
          return;
        }

        root.$kya.event('configurator', 'submitOrder', 'Submitted a purchase order.', {
          order: {
            ...purchaseOrder, name: null, email: null, company: null, notes: null // null out pii when submitting telemetry
          },
          ...am.value
        });

        // Reset cart
        additionalNotes.value = null;
        contactName.value = null;
        contactEmail.value = null;
        contactCompany.value = null;
        root.$store.commit('configurator/resetCart');

        // Redirect to main page
        root.$store.commit('configurator/setActivePage', 0);

        // Show snackback
        root.$buefy.snackbar.open({
          message: 'Request has been submitted. Kymeta will respond to your request shortly.',
          type: 'is-success',
          position: 'is-bottom',
          actionText: 'OK',
          indefinite: true
        });
      } catch (ex) {
        root.$buefy.snackbar.open({
          message: 'There was an error submitting your request.',
          type: 'is-danger',
          position: 'is-bottom',
          actionText: 'OK',
          indefinite: false
        });
      }
    };

    // fetch the cart item image (or return null when not found)
    const getItemAsset = (cartItem) => {
      // cart item was not provided
      if (!cartItem) return null;
      // match the product with available products
      const product = getProduct(cartItem.kpc);
      // product was not found
      if (!product) return null;
      // check for assets
      const assets = product.assets;
      // 
      if (assets == null) return null;
      // return first asset for the product
      return assets[0];
    }

    // acquire image asset CDN url from application configuration
    const assetCdnUrl = computed(() => root.$store.state.appConfig.assetCdnUrl);

    return {
      cart,
      getProduct,
      getItemAsset,
      getOption,
      isTerminal,
      isAccessory,
      isFinanced,
      hasConnectivity,
      deleteFromCart,
      cartTotal,
      submitOrder,
      additionalNotes,
      contactName,
      contactEmail,
      contactCompany,
      shippingCountry,
      submittingOrder,
      ConfiguratorCountries,
      shippingCountryIsOther,
      otherShippingCountry,
      wholesale,
      availableFinancingOptions,
      getCartItemTotals,
      getCartItemUnitPrice,
      getCartItemConnectivityPrice,
      getCartItemMonthly,
      getCartItemUpfrontCost,
      cartMonthlyTotal,
      cartUpfrontTotal,
      showFinancingTotals,
      showPrices,
      emptyCart,
      startOver,
      editCartItem,
      verifyWarranty,
      isNotMilitarySpec,
      am,
      productIsUnavailable,
      assetCdnUrl
    }
  }
}
</script>
<style lang="scss" scoped>
@import '../styles/colors';

.kpt-configurator-cart {
  overflow-y: auto;
  height: 100%;

  .configurator-cart, .configurator-upsell, .configurator-contact {
    background: white;
  }

  .configurator-cart-header {
    border-bottom: 1px solid $grey;
  }

  .kpt-configurator-divider {
    border-bottom: 1px solid $grey;
    margin-bottom: 0.5rem;
  }

  .kpt-configurator-item-wrapper {
    border-bottom: 1px solid $grey;
  }

  .kpt-configurator-item {
    display: grid;
    gap: 1rem;
    grid-template-columns: 300px 1fr 165px 165px;

    &:last-child {
      border-bottom: 0;
    }

    .kpt-configurator-cart-qty-mdfr {
      display: grid;
      grid-template-columns: 110px 160px 100px 1fr;
      padding-top: 0.75rem;
      padding-bottom: 0.75rem;
    }
    .kpt-configurator-cart-financing {
      display: grid;
      grid-template-columns: 110px 1fr;
      padding-top: 0.75rem;
      padding-bottom: 0.75rem;
      margin-bottom: 0.75rem;
    }
  }

  .configurator-upsell-grid {
    display: grid;
    gap: 1rem;
    grid-template-columns: 1fr 2px 1fr;

    .configurator-upsell-terminal, .configurator-upsell-accessory {
      padding: 2rem 6rem 1rem;
      display: grid;
      grid-template-columns: 1fr;
      grid-template-rows: 1fr 30px 30px;
      place-items: center;

      figure {
        max-width: 50%;
      }
    }
    .configurator-upsell-divider {
      background: $grey-lighter;
      margin-bottom: 1.5rem;
    }
  }

  .configurator-contact-information {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 1rem;
  }
}
</style>
